import { axiosGet, axiosPost, axiosPut } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { queryKeyMap } from '@/features/project/_utils/query-keys';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import {
  RelatedMaterialModel,
  type RelatedMaterialMutationParamsSchema,
  RelatedMaterialsModel,
} from '../../_models/related-material';

export const useRelatedMaterials = (projectId: string) => {
  return useQuery({
    queryKey: queryKeyMap.relatedMaterials(projectId),
    queryFn: async () => {
      const res = await axiosGet(
        `/api/projects/${projectId}/related-materials/`,
      );

      return RelatedMaterialsModel.parse(res);
    },
    enabled: !!projectId,
  });
};

export const useCreateRelatedMaterial = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (
      values: z.infer<typeof OthersFormSchema> & {
        attachmentIds: z.infer<
          typeof RelatedMaterialMutationParamsSchema
        >['attachmentIds'];
      },
    ) => {
      const { projectId, ...restValues } = values;
      const res = await axiosPost(
        `/api/projects/${projectId}/related-materials/`,
        restValues,
      );

      await invalidate(queryKeyMap.relatedMaterials(projectId));

      return RelatedMaterialModel.parse(res);
    },
  });
};

const OthersFormSchema = RelatedMaterialModel.pick({
  attachments: true,
}).extend({
  projectId: z.string().uuid(),
});

export const useUpdateRelatedMaterial = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (
      values: z.infer<typeof OthersFormSchema> & {
        id: z.infer<typeof RelatedMaterialModel.shape.id>;
        attachmentIds: z.infer<
          typeof RelatedMaterialMutationParamsSchema
        >['attachmentIds'];
      },
    ) => {
      const { projectId, id, ...restValues } = values;
      const res = await axiosPut(
        `/api/projects/${projectId}/related-materials/${id}/`,
        restValues,
      );

      await invalidate(queryKeyMap.relatedMaterials(projectId));

      return RelatedMaterialModel.parse(res);
    },
  });
};
